import { type Tree, logger } from "@nx/devkit";
import * as ts from "typescript";
import {
  findJsxAttribute,
  findJsxElements,
  getImportedAliases,
  hasSpreadAttribute,
  logSummary,
  parseTsx,
  visitTsxFiles,
} from "../utils/jsx-utils";

const PACKAGE_NAME = "@trackunit/react-form-components";
const COMPONENT_NAME = "ActionButton";

const ACTION_TYPE_TO_TITLE: Record<string, string> = {
  COPY: "Copy value",
  EDIT: "Edit value",
  EMAIL: "Send email",
  PHONE_NUMBER: "Call number",
  WEB_ADDRESS: "Open link",
};

const DEFAULT_TITLE = "Action";

const resolveTitleForType = (typeAttr: ts.JsxAttribute | null): string => {
  if (typeAttr === null) return DEFAULT_TITLE;
  const initializer = typeAttr.initializer;
  if (initializer === undefined) return DEFAULT_TITLE;
  if (ts.isStringLiteral(initializer)) {
    return ACTION_TYPE_TO_TITLE[initializer.text] ?? DEFAULT_TITLE;
  }
  return DEFAULT_TITLE;
};

const transformActionButtonUsage = (filePath: string, content: string): string | null => {
  const sourceFile = parseTsx(content, filePath);

  const aliases = getImportedAliases(sourceFile, PACKAGE_NAME);
  if (aliases === null) return null;

  const localAlias = Object.entries(aliases).find(([, name]) => name === COMPONENT_NAME)?.[0];
  if (localAlias === undefined) return null;

  const matches = findJsxElements(sourceFile, [localAlias]);
  if (matches.length === 0) return null;

  type Edit = { offset: number; text: string };
  const edits: Array<Edit> = [];

  for (const { openingElement } of matches) {
    if (hasSpreadAttribute(openingElement)) continue;
    if (findJsxAttribute(openingElement, "title") !== null) continue;

    const typeAttr = findJsxAttribute(openingElement, "type");
    const title = resolveTitleForType(typeAttr);
    edits.push({
      offset: openingElement.tagName.getEnd(),
      text: ` title="${title}"`,
    });
  }

  if (edits.length === 0) return null;

  edits.sort((a, b) => (a.offset === b.offset ? 0 : b.offset - a.offset));

  let updated = content;
  for (const { offset, text } of edits) {
    updated = updated.slice(0, offset) + text + updated.slice(offset);
  }
  return updated;
};

/**
 * Adds the now-required `title` prop to `<ActionButton>` usages that don't
 * already provide one. The default value is derived from the `type` prop when
 * possible (e.g. `COPY` → "Copy value"); consumers should replace the
 * placeholders with their own localized strings.
 */
export const actionButtonAddTitle = (tree: Tree): void => {
  const touched = visitTsxFiles(tree, "ActionButton", transformActionButtonUsage);
  logSummary("actionbutton-add-title", touched);
  if (touched > 0) {
    logger.info(`  Replace placeholder ActionButton titles with your own localized strings.`);
  }
};

export default actionButtonAddTitle;
